import { ResponsiveDialog, ResponsiveDialogProps } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material'
import { Form, Formik } from 'formik'
import { equals } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { object } from 'yup'

import {
  LabelEditorField,
  NameEditorField,
  fieldNameValidationSchemaByUserId,
  useLandReportView,
  useSaveDetail,
  useUpdateFieldMetadata,
} from '../../../../'

export type MetadataForm = {
  name: string
  labels?: string[]
}

interface FieldMetaFormProps extends ResponsiveDialogProps {
  onCancel: () => void
  onSuccess: (values: MetadataForm) => void
  onError?: (error: unknown) => void
  resourceId?: string
}

export const FieldMetaDialog = ({
  resourceId,
  onCancel,
  onError,
  onSuccess,
  ...dialogProps
}: FieldMetaFormProps) => {
  const { t } = useTranslation('@landmanager/FieldMetaDialog')
  const { field } = useLandReportView({ resourceId })
  const updateMetadataQuery = useUpdateFieldMetadata()
  const updateDetailQuery = useSaveDetail()

  const validationSchema = useMemo(
    () =>
      object().shape({
        name: fieldNameValidationSchemaByUserId({
          excludeResourceId: `${resourceId}`,
          fieldName: 'name',
          t,
          userId: field.data?.ownedBy?.userId,
        }),
      }),
    [t, field.data?.ownedBy?.userId, resourceId]
  )

  if (!field.data) {
    return null
  }

  const handleUpdateMetadata = async (values: MetadataForm) => {
    if (!!field.data && !!resourceId) {
      try {
        if (values.name !== field.data.name) {
          await updateDetailQuery.mutateAsync({
            resourceId,
            details: [{ traitId: 'name', input: { name: values.name } }],
          })
        }

        if (!equals(values.labels, field.data.metadata.labels)) {
          await updateMetadataQuery.mutateAsync({
            metadata: { labels: values.labels },
            // @ts-ignore
            resourceIds: [resourceId],
            action: 'replace',
          })
        }

        onSuccess(values)
      } catch (error) {
        onError && onError(error)
      }
    }
  }

  const resetEditorQueries = () => {
    updateDetailQuery.reset()
    updateMetadataQuery.reset()
  }

  const isLoading = updateDetailQuery.isLoading || updateMetadataQuery.isLoading

  const nameImmutable =
    field?.data?.resolveStandingDetail && field.data.resolveStandingDetail('name').immutable

  return (
    <Formik
      initialValues={
        {
          name: field.data.name,
          labels: field.data.labels,
        } as MetadataForm
      }
      validationSchema={validationSchema}
      onSubmit={handleUpdateMetadata}
      enableReinitialize
    >
      {({ dirty, resetForm, setFieldValue, submitForm, errors }) => (
        <Form>
          <ResponsiveDialog
            {...dialogProps}
            maxWidth={'sm'}
            TransitionProps={{
              onExited: () => {
                resetForm()
                resetEditorQueries()
              },
            }}
            fullWidth
          >
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>{t('name')}</FormLabel>
                  <NameEditorField name="name" disabled={nameImmutable} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('labels')}</FormLabel>
                  <LabelEditorField
                    labels={field.data?.labels}
                    onChange={labels => setFieldValue('labels', labels)}
                    disabled={isLoading}
                  />
                  {!!updateMetadataQuery.error && (
                    <Typography variant="caption" color="error">
                      {t('labelError')}
                      {' - '}
                      {/* @ts-ignore error is not appropriately typed */}
                      {t(updateMetadataQuery.error.response.data.message)}
                    </Typography>
                  )}
                </FormControl>
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button
                variant="text"
                disabled={isLoading}
                onClick={() => {
                  resetEditorQueries()
                  onCancel()
                }}
              >
                {t('cancel')}
              </Button>
              <LoadingButton
                loading={isLoading}
                type="submit"
                variant="contained"
                onClick={submitForm}
                disabled={
                  !dirty ||
                  typeof field.data?.can !== 'function' ||
                  !field.data.can(['edit_meta']) ||
                  !!errors.labels ||
                  !!errors.name
                }
              >
                {t('update')}
              </LoadingButton>
            </DialogActions>
          </ResponsiveDialog>
        </Form>
      )}
    </Formik>
  )
}
